import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  getCohorts,
  getGetCohortsQueryKey,
} from 'api/generated/cohorts/cohorts';
import {
  getGetGlobalSettingsQueryKey,
  getGlobalSettings,
  updateGlobalSettings,
} from 'api/generated/global-settings/global-settings';
import type { ClientCohortDto, GlobalSettingsDto } from 'api/generated/schemas';
import type { ApiErrorWithMeta } from 'api/interceptors';

import { useCurrentUser } from 'hooks/api/use-current-user';

const useOktaUserId = (): string => {
  const user = useCurrentUser();

  return user.data?.oktaUserId ?? '';
};

export const useCohorts = (): UseQueryResult<
  ClientCohortDto[],
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();

  return useQuery<ClientCohortDto[], ApiErrorWithMeta>({
    queryKey: getGetCohortsQueryKey(oktaUserId),
    queryFn: ({ signal }) => getCohorts(oktaUserId, undefined, signal),
    enabled: !!oktaUserId,
  });
};

export const useGlobalSettings = (): UseQueryResult<
  GlobalSettingsDto,
  ApiErrorWithMeta
> => {
  const oktaUserId = useOktaUserId();

  return useQuery<GlobalSettingsDto, ApiErrorWithMeta>({
    queryKey: getGetGlobalSettingsQueryKey(oktaUserId),
    queryFn: ({ signal }) => getGlobalSettings(oktaUserId, undefined, signal),
    enabled: !!oktaUserId,
  });
};

export const useUpdateGlobalSettings = (): UseMutationResult<
  GlobalSettingsDto,
  ApiErrorWithMeta,
  GlobalSettingsDto
> => {
  const oktaUserId = useOktaUserId();
  const queryClient = useQueryClient();

  return useMutation<GlobalSettingsDto, ApiErrorWithMeta, GlobalSettingsDto>({
    mutationFn: (body) => updateGlobalSettings(oktaUserId, body),
    onSuccess: (persisted) => {
      queryClient.setQueryData(
        getGetGlobalSettingsQueryKey(oktaUserId),
        persisted
      );
    },
  });
};
